import React, { useState } from 'react';

import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Stack from '@mui/material/Stack';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AddIcon from '@mui/icons-material/Add';

import AddDistributionDialog from './AddDistributionDialog';
import AddPrintsDistributionDialog from './AddPrintsDistributionDialog';
import axiosInstance from '../../api/axiosInstance';

export default function DistributionsCollapse({ open, entry, accounts }) {
    const [distributions, setDistributions] = useState(entry.distributions);
    const [printsdistributions, setPrintsDistributions] = useState(entry.printsdistributions);
    const [isDistributionDialogOpen, setDistributionDialogOpen] = React.useState(false);
    const [isPrintsDistributionDialogOpen, setPrintsDistributionDialogOpen] = React.useState(false);

    // Gestion de l'ouverture et de la fermeture du Dialog pour ajouter une part ('distribution')
    const handleDistributionDialogOpen = () => setDistributionDialogOpen(true);
    const handleDistributionDialogClose = () => setDistributionDialogOpen(false);

    // Gestion de l'ouverture et de la fermeture du Dialog pour ajouter une part d'impression ('printsdistribution')
    const handlePrintsDistributionDialogOpen = () => setPrintsDistributionDialogOpen(true);
    const handlePrintsDistributionDialogClose = () => setPrintsDistributionDialogOpen(false);

    // Gestion de la mise à jour des parts/'distributions' affichées après ajout
    const handleUpdateDistributions = (newDistributions) => setDistributions(distributions.concat(newDistributions));

    // Gestion de la mise à jour des parts/'distributions' d'impressions affichées après ajout
    const handleUpdatePrintsDistributions = (newPrintsDistributions) => setPrintsDistributions(printsdistributions.concat(newPrintsDistributions));

    // Gestion de la suppression des parts/'distributions' (appel de l'API et mise à jour du state)
    const handleDeleteDistributions = async (distributions_ids) => {
        const distributions_to_delete = { distributions_ids: distributions_ids };
        try {
            await axiosInstance.post(`api/entries/${entry.id}/distributions/delete/`, distributions_to_delete);
        } catch (error) {
            console.error("Erreur lors de la suppression de/des part(s) :", error);
        } finally {
            // Mise à jour du state
            const filteredDistributions = distributions.filter(distribution => !distributions_ids.includes(distribution.id));
            setDistributions(filteredDistributions);
        }
    };

    // Gestion de la suppression des parts d'impression (appel de l'API et mise à jour du state)
    const handleDeletePrintsDistributions = async (printsdistributions_ids) => {
        const printsdistributions_to_delete = { printsdistributions_ids: printsdistributions_ids };
        try {
            await axiosInstance.post(`api/entries/${entry.id}/printsdistributions/delete/`, printsdistributions_to_delete);
        } catch (error) {
            console.error("Erreur lors de la suppression de/des part(s) d'impression :", error);
        } finally {
            // Mise à jour du state
            const filteredPrintsDistributions = printsdistributions.filter(printsdistribution => !printsdistributions_ids.includes(printsdistribution.id));
            setPrintsDistributions(filteredPrintsDistributions);
        }
    };

    // Gestion de la répartition d'une Entry selon les loyers (appel de l'API et mise à jour du state)
    const handleRentsDistributions = async () => {
        try {
            // appel API
            const response = await axiosInstance.post(`api/entries/${entry.id}/distributions/rents/`);
            // Mise à jour du state
            setDistributions(response.data);
        } catch (error) {
            console.error("Erreur lors de la création des parts de loyers :", error);
        }
    };

    // Gestion de la réparition d'une Entry entre les locataires (appel de l'API et mise à jour du state)
    const handleTenantsDistributions = async () => {
        try {
            // Appel de l'API
            const response = await axiosInstance.post(`api/entries/${entry.id}/distributions/tenants/`);
            // Mise à jour du state
            setDistributions(response.data);
        } catch (error) {
            console.error("Erreur lors de la création des parts des locataires :", error);
        }
    };

    const status = { 'tenant': 'Locataire', 'sublet': 'Sous-locataire', 'legal': 'Domicilié' };

    return (
        <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <Box sx={{ margin: 1 }}>
                        <Stack direction="column" spacing={2}>
                            <Typography variant="h6" gutterBottom component="div">
                                Répartition
                                <IconButton aria-label="delete" size="large" onClick={() => handleDeleteDistributions(distributions.map(distribution => distribution.id))}>
                                    <DeleteIcon color="error" fontSize="inherit" />
                                </IconButton>
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Button variant="contained" size="small" onClick={handleRentsDistributions} endIcon={<AutoFixHighIcon />}>
                                    Loyers
                                </Button>
                                <Button variant="contained" size="small" onClick={handleTenantsDistributions} endIcon={<AutoFixHighIcon />}>
                                    Locataires
                                </Button>
                                <Button onClick={handleDistributionDialogOpen} variant="contained" size="small" endIcon={<AddIcon />}>
                                    Ajouter une part
                                </Button>
                                <AddDistributionDialog
                                    open={isDistributionDialogOpen}
                                    onUpdate={handleUpdateDistributions}
                                    onClose={handleDistributionDialogClose}
                                    entry={entry}
                                    accounts={accounts} />
                            </Stack>
                            <Table size="small" aria-label="distributions">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Compte</TableCell>
                                        <TableCell>Statut</TableCell>
                                        <TableCell align="right">Montant (€)</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {distributions && distributions.length > 0 ? (
                                        distributions.map((distributionRow) => (
                                            <TableRow key={distributionRow.id}>
                                                <TableCell component="th" scope="row">
                                                    {accounts.find((acc) => acc.id === distributionRow.account)?.full_name || "Compte introuvable"}
                                                </TableCell>
                                                <TableCell>
                                                    {status[accounts.find((acc) => acc.id === distributionRow.account)?.contract] || "Statut introuvable"}
                                                </TableCell>
                                                <TableCell align="right">{distributionRow.amount.toLocaleString('fr-FR', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}</TableCell>
                                                <TableCell align='right'>
                                                    <IconButton onClick={() => handleDeleteDistributions([distributionRow.id])} aria-label="delete" size="small">
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                Aucune répartition définie
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                            <Typography variant="h6" gutterBottom component="div">
                                Impressions
                                <IconButton aria-label="delete" size="large" onClick={() => handleDeletePrintsDistributions(printsdistributions.map(printsdistribution => printsdistribution.id))}>
                                    <DeleteIcon color="error" fontSize="inherit" />
                                </IconButton>
                            </Typography>

                            <Stack direction="row" spacing={2}>
                                <Button onClick={handlePrintsDistributionDialogOpen} variant="contained" size="small" endIcon={<AddIcon />}>
                                    Ajouter une part d'impressions
                                </Button>
                                <AddPrintsDistributionDialog
                                    open={isPrintsDistributionDialogOpen}
                                    onUpdate={handleUpdatePrintsDistributions}
                                    onClose={handlePrintsDistributionDialogClose}
                                    entry={entry}
                                    accounts={accounts} />
                            </Stack>
                            {/* Table des Impressions */}
                            <Table size="small" aria-label="printsdistributions">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Compte</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Quantité</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {printsdistributions && printsdistributions.length > 0 ? (
                                        printsdistributions.map((printsdistributionRow) => (
                                            <TableRow key={printsdistributionRow.id}>
                                                <TableCell component="th" scope="row">
                                                    {accounts.find((acc) => acc.id === printsdistributionRow.account)?.full_name || "Compte introuvable"}
                                                </TableCell>
                                                <TableCell>
                                                    {printsdistributionRow.type}
                                                </TableCell>
                                                <TableCell align="right">{printsdistributionRow.amount}</TableCell>
                                                <TableCell align='right'>
                                                    <IconButton onClick={() => handleDeletePrintsDistributions([printsdistributionRow.id])} aria-label="delete" size="small">
                                                        <DeleteIcon fontSize="inherit" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                Aucune répartition définie
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Stack>
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};